import { useEffect, useMemo, useState } from 'react';
import { GameStateStatus, isCardPlayable, PlayerTableViewModelV2, PlayingGameState } from 'shared';
import { V2apiClient } from '../../ApiClient';
import { useApiCall } from '../../useApiCall';
import { computeSeats } from './board/seat-layout';
import { Seat } from './board/Seat';

/*
 * Players — renders every seat around the table from computeSeats(n). Works for
 * any player count (n = tablePlayers.length). In-game the view model is
 * caller-relative so the local player is seat 0 (bottom); pre-game seats are in
 * real order so clicking an open seat moves you into that exact chair. Either
 * way, table.seatIndexes[slot] gives the real server seat for each display slot,
 * which the seat picker uses to target the seat a player clicks.
 *
 * Card clicks are checked against the shared playability rule before hitting the
 * server: a legal card is played; an illegal one instead flags every unplayable
 * card with a red outline (no server call). The flags clear on a valid play or
 * when the turn/trick changes.
 */

interface Props {
  table?: PlayerTableViewModelV2;
}

export const Players: React.FC<Props> = ({ table }) => {
  const apiCall = useApiCall();
  const n = table?.tablePlayers.length ?? 0;
  const seats = useMemo(() => computeSeats(n), [n]);
  const [illegalCardIds, setIllegalCardIds] = useState<Set<string>>();
  // The card whose play is in flight, so the fan can animate it out before the
  // server's update removes it.
  const [playingCardId, setPlayingCardId] = useState<string>();

  const gs = table?.gameState;
  // A spectator has no seat: slot 0 is seat 0 (not them), so it never grants a
  // turn, no seat is "local" (all hands face-down), and nothing is interactive.
  const spectating = !!table?.isSpectator;
  const localTurn = !spectating && !!table?.tablePlayers[0]?.isCurrentTurn;
  const trickSuite = (gs as PlayingGameState | undefined)?.trickSuite;
  // Clear the flags at the start of each turn, and once a valid play changes the
  // hand or trick (the hand ids collapse to a stable key we can watch).
  const handKey = (table?.playerHand ?? []).map((c) => c.id).join(',');
  useEffect(() => {
    setIllegalCardIds(undefined);
    setPlayingCardId(undefined);
  }, [localTurn, trickSuite, gs?.status, handKey]);

  if (!table) return null;

  // While a trick is being scored the winner is raking it in; their avatar
  // marks where TrickArea's pile is sweeping to.
  const collectingPlayerId =
    gs?.status === GameStateStatus.ScoringTrick
      ? table.tableCards?.find((c) => c.isWinner)?.playerId
      : undefined;

  const canPlay =
    !spectating &&
    table.gameState?.status !== GameStateStatus.Bidding &&
    !!table.tablePlayers[0]?.isCurrentTurn;
  // Before the game starts, empty seats can be taken to pick a seat/team. A
  // seated player moves seats (changeSeat); a spectator (re)joins the table by
  // taking one (sitDown). Either way the empty "Sit here" buttons are shown.
  const canChooseSeat = !table.gameState;
  const takeSeat = (realSeatIndex: number) => {
    void apiCall(
      spectating ? V2apiClient.sitDown(realSeatIndex) : V2apiClient.changeSeat(realSeatIndex),
    );
  };

  const handlePlay = (cardId: string) => {
    const hand = table.playerHand ?? [];
    const card = hand.find((c) => c.id === cardId);
    if (!card || !table.gameState) return;

    const ctx = {
      status: table.gameState.status,
      trickSuite: (table.gameState as PlayingGameState).trickSuite,
      trumpSuite: (table.gameState as PlayingGameState).trumpSuite,
      handSuites: hand.map((c) => c.suite),
    };

    if (isCardPlayable(card.suite, ctx)) {
      setIllegalCardIds(undefined);
      setPlayingCardId(cardId);
      void apiCall(V2apiClient.playCard(cardId)).then((ok) => {
        // The card is still in hand — put it back rather than leaving a gap.
        if (!ok) setPlayingCardId(undefined);
      });
    } else {
      // Show the red border on every card that can't be played right now.
      setIllegalCardIds(
        new Set(hand.filter((c) => !isCardPlayable(c.suite, ctx)).map((c) => c.id)),
      );
    }
  };

  return (
    <>
      {seats.map((seat) => {
        const player = table.tablePlayers[seat.index];
        // For a spectator no seat is local, so seat 0 renders face-down like the
        // rest rather than showing a (nonexistent) local hand.
        const isLocal = seat.isLocal && !spectating;
        return (
          <Seat
            key={seat.index}
            seat={{ ...seat, isLocal }}
            player={player}
            localHand={isLocal ? table.playerHand : undefined}
            canPlay={canPlay}
            onPlay={handlePlay}
            illegalCardIds={isLocal ? illegalCardIds : undefined}
            playingCardId={isLocal ? playingCardId : undefined}
            isCollecting={!!player && player.id === collectingPlayerId}
            onTakeSeat={
              canChooseSeat && !player ? () => takeSeat(table.seatIndexes[seat.index]) : undefined
            }
          />
        );
      })}
    </>
  );
};
